/* Offline synthesis used by the audio engine, the virtual microphone and the DSP tests. */
import type { Rng } from './rng';

export interface Synthesized {
  data: Float32Array<ArrayBuffer>;
  /** Pitch the samples actually have; play back at rate `freq / actualFreq` for exact tuning. */
  actualFreq: number;
}

export interface PluckOptions {
  rand?: Rng;
  brightness?: number;
  pickPos?: number;
  /** Seconds to decay by 60 dB; defaults by register. */
  t60?: number;
}

/** Karplus-Strong plucked string. */
export function ksPluck(freq: number, sr: number, dur: number, o: PluckOptions = {}): Synthesized {
  const rnd = o.rand || Math.random;
  const len = Math.max(1, Math.floor(sr * dur));
  const out = new Float32Array(len);
  const S = 0.5;
  const N = Math.max(3, Math.round(sr / freq + S));
  const actualFreq = sr / (N - S);
  const line = new Float32Array(N);
  const bright = o.brightness == null ? 0.55 : o.brightness;
  let lp = 0;
  for (let i = 0; i < N; i++) {
    lp += bright * (rnd() * 2 - 1 - lp);
    line[i] = lp;
  }
  let mean = 0;
  for (let i = 0; i < N; i++) mean += line[i];
  mean /= N;
  for (let i = 0; i < N; i++) line[i] -= mean;
  const pp = o.pickPos == null ? 0.18 : o.pickPos;
  const d = Math.max(1, Math.round(pp * N));
  const tmp = line.slice();
  for (let i = 0; i < N; i++) line[i] = tmp[i] - 0.85 * tmp[(i - d + N) % N];
  let mx = 0;
  for (let i = 0; i < N; i++) mx = Math.max(mx, Math.abs(line[i]));
  if (mx > 0) for (let i = 0; i < N; i++) line[i] /= mx;
  const t60 = o.t60 || (freq < 120 ? 5.0 : freq < 250 ? 3.8 : freq < 500 ? 2.8 : 2.0);
  const g = 0.001 ** (1 / (actualFreq * t60));
  let idx = 0;
  for (let n = 0; n < len; n++) {
    const cur = line[idx];
    const j = idx + 1 === N ? 0 : idx + 1;
    out[n] = cur;
    line[idx] = g * ((1 - S) * cur + S * line[j]);
    idx = j;
  }
  const fade = Math.min(len, Math.floor(sr * 0.0015));
  for (let i = 0; i < fade; i++) out[i] *= i / fade;
  return { data: out, actualFreq };
}

export interface PianoOptions {
  inharm?: number;
  tau?: number;
}

/** Soft piano / electric-piano tone via additive synthesis with per-partial decay. */
export function pianoTone(freq: number, sr: number, dur: number, o: PianoOptions = {}): Synthesized {
  const len = Math.max(1, Math.floor(sr * dur));
  const out = new Float32Array(len);
  const B = o.inharm == null ? 0.00012 : o.inharm;
  const baseTau = o.tau || (freq < 200 ? 1.8 : freq < 500 ? 1.3 : 0.9);
  for (let h = 1; h <= 16; h++) {
    const fh = freq * h * Math.sqrt(1 + B * h * h);
    if (fh > sr * 0.42) break;
    const amp = h ** -1.25 * (h === 2 ? 0.8 : 1) * (h > 7 ? 0.6 : 1);
    const tau = baseTau / (1 + 0.45 * (h - 1));
    const w = (2 * Math.PI * fh) / sr;
    const cw = Math.cos(w);
    const sw = Math.sin(w);
    const dec = Math.exp(-1 / (tau * sr));
    let c = 1;
    let s = 0;
    let env = amp;
    for (let n = 0; n < len; n++) {
      out[n] += env * s;
      const nc = c * cw - s * sw;
      s = s * cw + c * sw;
      c = nc;
      env *= dec;
      if (env < 1e-6) break;
    }
  }
  const att = Math.floor(sr * 0.004);
  for (let i = 0; i < Math.min(att, len); i++) out[i] *= i / att;
  let mx = 0;
  for (let i = 0; i < len; i++) mx = Math.max(mx, Math.abs(out[i]));
  if (mx > 0) {
    const gN = 0.9 / mx;
    for (let i = 0; i < len; i++) out[i] *= gN;
  }
  return { data: out, actualFreq: freq };
}
